import React, { useRef, useMemo, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import cx from "classnames";
import { withTranslation } from "react-i18next";
import ErrorBlock from "components/CustomerPage/Parameters/components/ErrorBlock.js";
import { isEmpty, get } from "lodash";
import SmartPortal from "../SmartPortal";
import ReactSVG from "react-svg";
import "./styles.scss";

const { Option } = components;

const CustomCollapseOption = props => {
    const { data } = props;

    // add custom class names based on custom flags
    const extraClass = cx({
        "comboBox__option--collapsed": data.collapsed,
        "comboBox__option--toggle-options": data.isToggleOptions,
        "comboBox__option--collapsed-first": data.isFirstCollapsed,
        "with-collapsed-options": true,
        // "comboBox__option--visible": data.isVisible, // maybe we need that in case there is some option selected which is not visible?
    });

    // use the default Option to keep all base behavior
    return <Option {...props} className={cx(props.className, extraClass)} />;
};

const ComboBox = props => {
    const {
        t,
        label,
        options,
        input,
        meta,
        placeholder = t("dontSelect"),
        // showValuesCount,
        id,
        required,
        searchable,
        // filterOption,
        // noborder,
        noResultsText = t("noResultsFound"),
        // maxLength,
        inputProps,
        // refKey,
        // formValues,
        clearAllText = t("clear all results"),
        hasExValue = false,
        onRefReady,
        customSelectPortalClassName,
        clearable = true,
        isAsync,
        isSelectPortalMenuFlexible,
        svgIconPath,
        withCollapsedOptions = false, // ttwos custom collapse option logic
        disabled: isDisabled,
        ...restProps
    } = props;
    const selectRef = useRef();
    const inputRef = useRef();
    const [searchQuery, setSearchQuery] = useState(""); // we need to set search query to properly trigger a component rerender - that will keep out portal in correct position
    const [showHiddenOptions, setShowHiddenOptions] = useState(false);

    let fallbackAsyncValue;
    let fallbackAsyncOptions = [];
    if (isAsync) {
        fallbackAsyncValue = get(input, "value");
        if (fallbackAsyncValue && fallbackAsyncValue.label && !options) {
            fallbackAsyncOptions = [fallbackAsyncValue];
        }
    }

    const visibleOptions = useMemo(() => {
        if (!options || !Array.isArray(options)) return [];

        if (!withCollapsedOptions) {
            return options;
        }

        // simply return options if no collapsed options provided (no need in show all button)
        const isCollapsedAvailable = options.some(option => option.collapsed);
        if (!isCollapsedAvailable) {
            return options.map(option => ({ ...option, withoutCollapsedOptions: true }));
        }

        let visible = showHiddenOptions ? options : options.filter(o => !o.collapsed);

        if (!showHiddenOptions && isCollapsedAvailable) {
            visible = [
                ...visible,
                {
                    label: t("showAll") || "Show hidden options",
                    value: "__toggle_collapse_options__",
                    isToggleOptions: true,
                },
            ];
        } else {
            const firstCollapsedIndex = visible.findIndex(option => option.collapsed);
            visible = visible.map((item, index) => {
                if (index === firstCollapsedIndex) {
                    return { ...item, isFirstCollapsed: true };
                }
                return item;
            });
        }
        return visible;
    }, [options, showHiddenOptions, t]);

    const isCollapsedAvailable = useMemo(() => {
        if (!options || !Array.isArray(options)) {
            return false;
        }
        return options.some(option => option.collapsed);
    }, [options, withCollapsedOptions]);

    const disabledFilterOption = option => {
        if (option && option.value && input && input.value) {
            return option.value === input.value;
        }
        // there is no value in single select = return no options
        if (!input || [null, undefined, ""].includes(input.value)) {
            return false;
        }
        return ![null, undefined, ""].includes(option ? option.value : null);
    };

    const filterOption = isDisabled ? disabledFilterOption : props.filterOption;

    useEffect(() => {
        if (onRefReady) {
            onRefReady(selectRef);
        }
    }, [onRefReady]);

    // useEffect(() => {
    //     const master = get(formValues, `blocks[${refKey}]`) || {};
    //     const masterPrev = get(formValues, `blocks[${refKey}]`) || {};

    //     if (refKey && master.value !== masterPrev.value) {
    //         if (
    //             !options.some(
    //                 i => i.refVal.includes(master.value || master) && i.value === (input.value.value || input.value)
    //             )
    //         ) {
    //             input.onChange(null);
    //         }
    //     }

    //     if (refKey && !isEmpty(master) && ![null, undefined, ""].includes(input.value)) {
    //         if (
    //             !options.some(
    //                 i => i.refVal.includes(master.value || master) && i.value === (input.value.value || input.value)
    //             )
    //         ) {
    //             input.onChange(null);
    //         }
    //     }
    // }, [refKey, formValues, options, input]);

    // const arrowRender = values => (values.isOpen ? <i className="icon-up" /> : <i className="icon-down" />);

    const onFocus = event => {
        input && input.onFocus(event);
    };

    const onChange = (data, other, otherOther) => {
        // handle our special toggle entry
        if (withCollapsedOptions && data.value === "__toggle_collapse_options__") {
            // toggle collapsed options
            setShowHiddenOptions(prev => !prev);

            // immediately re-open and re-focus after state update
            setTimeout(() => {
                if (selectRef.current) {
                    // Keep menu open
                    if (typeof selectRef.current.onMenuOpen === "function") {
                        selectRef.current.onMenuOpen();
                    }
                    // Restore focus
                    if (typeof selectRef.current.focus === "function") {
                        selectRef.current.focus();
                    }
                }
            }, 0);

            return;
        }
        if (input) {
            const value = hasExValue ? data : get(data, "value", null);
            input.onChange(value);
        }
    };

    // const master = get(formValues, `blocks[${refKey}]`);
    // let selectOption = options;

    // if (refKey && master) {
    //     selectOption = selectOption.filter(i => i.refVal.includes(master.value || master));
    // }

    const selectClassName = cx(
        "container-comboBox",
        meta && meta.touched && (meta.error || meta.warning) && "input-field__error",
        meta && meta.active && "active",
        input && input.value && "not-empty",
        svgIconPath && "with-svg-icon"
        // { noborder }
    );

    const showErrorBlock = meta && meta.touched && !meta.active && meta.error;

    const MenuPortal = useCallback(
        props => {
            // do not render empty disabled options menu
            // if (
            //     restProps.disabled &&
            //     ((options && options.length === 0) || !input || [null, undefined, ""].includes(input.value))
            // ) {
            //     return null;
            // }
            // do not render menu if disabled
            if (isDisabled) {
                return null;
            }
            return (
                <SmartPortal
                    portalClassName={cx("portal-combobox-menu", {
                        [customSelectPortalClassName]: customSelectPortalClassName,
                        "options-disabled": isDisabled,
                    })}
                    targetElement={inputRef && inputRef.current}
                    portalPlacement="bottom inner-right"
                    portalKey={"SmartPortalComboboxMenu"}
                    fieldKey={input && input.name}
                    onClickOutside={handleSmartPortalClickOutside}
                    outerOffset={4}
                    withTargetWidth
                    isSelectPortalMenuFlexible={isSelectPortalMenuFlexible}
                >
                    {props.children}
                </SmartPortal>
            );
        },
        [inputRef, isDisabled]
    );

    const selectedValue = hasExValue
        ? options && options.find(c => c.value === get(input, "value.value"))
        : options && options.find(c => c && input && c.value === input.value);

    function handleInputChange(inputValue, action) {
        if (isDisabled) {
            return;
        }
        let value = inputValue;
        if (inputProps && inputProps.maxLength && inputValue.length > inputProps.maxLength) {
            value = inputValue.substr(0, inputProps.maxLength);
        }
        const onInputChange =
            props.onInputChange && typeof props.onInputChange === "function" ? props.onInputChange : () => {};
        if (action?.action !== "input-blur" && action?.action !== "menu-close") {
            onInputChange(value, action);
            setSearchQuery(value);
        }
        if (action?.action === "input-blur" && props.onBlurResetsInput) {
            setSearchQuery("");
            onInputChange("", action);
        }
        if (action?.action === "menu-close" && props.onCloseResetsInput) {
            setSearchQuery("");
            onInputChange("", action);
        }
    }

    function handleClose(...args) {
        if (props && props.onClose && typeof props.onClose === "function") {
            props.onClose(...args);
        }
    }

    function handleBlur(...args) {
        if (props && props.onBlur && typeof props.onBlur === "function") {
            props.onClose(...args);
        }
        input && input.onBlur();
    }

    function handleSmartPortalClickOutside() {
        // onMenuClose
        selectRef &&
            selectRef.current &&
            typeof selectRef.current.onMenuClose === "function" &&
            selectRef.current.onMenuClose();

        // blur
        selectRef && selectRef.current && typeof selectRef.current.blur === "function" && selectRef.current.blur();
        handleBlur();
    }

    useEffect(() => {
        if (svgIconPath && inputRef && inputRef.current) {
            const el = inputRef.current.querySelector(".comboBox__control");
            if (el) {
                el.style.backgroundImage = `url("${svgIconPath}")`;
            }
        }
    }, [svgIconPath]);

    const components = { MenuPortal };

    if (withCollapsedOptions && isCollapsedAvailable) {
        components.Option = CustomCollapseOption;
    }

    return (
        <div id={id} className="input-element" ref={inputRef}>
            {label && (
                <div className="input-label">
                    {label}
                    {required && <span className="required-field">*</span>}
                </div>
            )}

            <Select
                {...restProps}
                {...input}
                {...meta}
                inputValue={searchQuery}
                // inputProps={inputProps} // removed, limit input length by onInputChange method
                options={visibleOptions || fallbackAsyncOptions}
                className={selectClassName}
                searchable={searchable}
                onBlur={handleBlur}
                onFocus={onFocus}
                onChange={onChange}
                placeholder={placeholder}
                optionClassName="select-option"
                noOptionsMessage={() => noResultsText}
                filterOption={filterOption}
                menuPlacement="auto"
                menuPosition="fixed"
                hasExValue={hasExValue}
                components={components}
                onInputChange={handleInputChange}
                value={selectedValue || fallbackAsyncValue || null}
                onMenuClose={handleClose}
                ref={selectRef}
                classNamePrefix="comboBox"
                backspaceRemovesValue={!isDisabled && clearable}
                isClearable={!isDisabled && clearable}
                openMenuOnClick={!isDisabled}
                onMenuOpen={props.onMenuOpen && typeof props.onMenuOpen === 'function' ? props.onMenuOpen : () => {}}
                // isDisabled={props.disabled}
            />

            {showErrorBlock && <ErrorBlock error={meta.error} className="m-r-10" />}
        </div>
    );
};

ComboBox.propTypes = {
    options: PropTypes.array,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    input: PropTypes.object,
    meta: PropTypes.object,
    searchable: PropTypes.bool,
    required: PropTypes.bool,
    noResultsText: PropTypes.string,
    filterOption: PropTypes.func,
    // noborder: PropTypes.bool,
    onRefReady: PropTypes.func,
    t: PropTypes.func,
    formValues: PropTypes.object,
    refKey: PropTypes.string,
    // maxLength: PropTypes.number,
    clearAllText: PropTypes.string,
    hasExValue: PropTypes.bool,
};

export default withTranslation()(ComboBox);
